/**
 * Credit Block Bureau React Query hooks
 * Fetches Credit_Bureaus__c (RDS custom_credit_bureaus) from Gobodo per client.
 * Client → Bureau graph traversal only (credit_bureaus_client_rel); no bulk list fallback.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  graphKeyFromCreatedRecord,
  UNLOCK_REQUEST_REL_FIELD_ON_CASE,
} from '../creditBlock/creditBlockCaseCreate';
import { updateRecord, createRecord } from '../recordStorageService';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { BureauCredential } from '../../types';
import { getBureausViaTraversalFromClient } from './creditBlockQueries';
import { resolveGraphUserNamesBySfIds, looksLikeGraphUserSfId } from '../gobodo/gobodoUserSfIdLookup';
import { logRecordCreated } from '../activityLogService';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const BUREAU_OBJECT = `Credit_Bureaus${CUSTOM_SUFFIX}`;
const BUREAUS = ['chexsystems', 'equifax', 'experian', 'transunion'] as const;

const normalizeBureau = (val: unknown): (typeof BUREAUS)[number] => {
  const s = String(val ?? '').toLowerCase();
  if (s.includes('chex')) return 'chexsystems';
  if (s.includes('equifax')) return 'equifax';
  if (s.includes('experian')) return 'experian';
  if (s.includes('transunion') || s.includes('trans union')) return 'transunion';
  return 'equifax';
};

const normalizeDateValue = (val: unknown): string | undefined => {
  const raw = String(val ?? '').trim();
  if (!raw) return undefined;
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number(raw);
    const ms = raw.length === 10 ? n * 1000 : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
  }
  const yyyyMmDd = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (yyyyMmDd) return yyyyMmDd[1];
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
};

const mapToBureauCredential = (r: Record<string, unknown>): BureauCredential => {
  const raw = r as Record<string, unknown>;
  const data = (raw.data as Record<string, unknown>) ?? raw;
  const statusRaw = String(
    data.Online_Account_Status__c ??
      data.Stage__c ??
      data.Status__c ??
      data.stage ??
      data.status ??
      data.custom__online_account_status ??
      data.custom__stage ??
      raw.Online_Account_Status__c ??
      raw.Stage__c ??
      ''
  ).trim().toLowerCase();
  const statusMap: Record<string, BureauCredential['status']> = {
    locked: 'locked', unlocked: 'unlocked', monitored: 'monitored', unknown: 'unknown',
    blocked: 'locked', unblocked: 'unlocked',
    active: 'unlocked', enrolled: 'unlocked', registered: 'unlocked',
    inactive: 'locked', suspended: 'locked',
    permanently_unblocked: 'permanently_unblocked', 'permanently unblocked': 'permanently_unblocked',
  };
  const status = statusMap[statusRaw] ?? (['locked', 'unlocked', 'monitored', 'permanently_unblocked', 'unknown'].includes(statusRaw) ? (statusRaw as BureauCredential['status']) : undefined);
  const authorityRaw = String(data.Authority__c ?? data.authority ?? raw.Authority__c ?? '').toLowerCase();
  const authority = authorityRaw === 'primary' ? 'primary' : authorityRaw === 'secondary' ? 'secondary' : undefined;
  const permUnblockVal = data.PermUnblockDate__c ?? data.permUnblockDate ?? raw.PermUnblockDate__c;
  const portalCreationVal = data.PortalCreationDate__c ?? data.portalCreationDate ?? data.custom__portal_creation_date ?? raw.PortalCreationDate__c;
  const pinMailedVal =
    data.PIN_Mailed__c ??
    data.PINsMailed__c ??
    data.pinMailed ??
    raw.PIN_Mailed__c ??
    raw.PINsMailed__c;
  const challengeQ = data.ChallengeQuestion__c ?? data.challengeQuestion ?? raw.ChallengeQuestion__c;
  const fourDigitPin =
    data.X4_Digit_PIN__c ??
    data.FourDigitPIN__c ??
    data.fourDigitPIN ??
    data.fourDigitPin ??
    raw.X4_Digit_PIN__c ??
    raw.FourDigitPIN__c ??
    raw.fourDigitPin;
  const challengeA =
    data.ChallengeAnswer__c ??
    data.Challenge_Answer__c ??
    data.challengeAnswer ??
    raw.ChallengeAnswer__c ??
    raw.Challenge_Answer__c ??
    raw.challengeAnswer;
  const tri = (v: unknown): boolean | undefined => {
    if (v === true || v === 'true' || v === 1) return true;
    if (v === false || v === 'false' || v === 0) return false;
    return undefined;
  };
  const blockProc =
    data.Block_Process_Collection__c ?? data.blockProcessCollection ?? raw.Block_Process_Collection__c;
  const unblockProc =
    data.Unblock_Process_Collection__c ?? data.unblockProcessCollection ?? raw.Unblock_Process_Collection__c;
  const unblockStatusRaw = String(
    data.UnblockRequestStatus__c ?? data.unblockRequestStatus ?? ''
  ).toLowerCase();
  const unblockRequestStatus = ['pending', 'approved', 'denied', 'completed'].includes(
    unblockStatusRaw
  )
    ? (unblockStatusRaw as BureauCredential['unblockRequestStatus'])
    : undefined;
  const startVal =
    data.StartDate__c ??
    data.EnrollmentDate__c ??
    data.enrollmentDate ??
    data.custom__start_date ??
    data.custom__portal_creation_date ??
    raw.StartDate__c ??
    raw.EnrollmentDate__c;
  const endVal =
    data.EndDate__c ??
    data.UnblockDate__c ??
    data.unblockDate ??
    data.custom__end_date ??
    raw.EndDate__c ??
    raw.UnblockDate__c;
  return {
    creditBureauId: String(raw.id ?? raw.key ?? data.Id ?? '').trim() || undefined,
    authority,
    status,
    portalCreationDate: normalizeDateValue(portalCreationVal),
    enrollmentDate: normalizeDateValue(startVal),
    startDate: normalizeDateValue(data.StartDate__c ?? data.startDate ?? raw.StartDate__c),
    unblockDate: normalizeDateValue(endVal),
    endDate: normalizeDateValue(data.EndDate__c ?? data.endDate ?? raw.EndDate__c),
    permUnblockDate: normalizeDateValue(permUnblockVal),
    unblockRequestStatus,
    username: String(data.Username__c ?? data.UserName__c ?? data.username ?? raw.Username__c ?? raw.UserName__c ?? '').trim() || undefined,
    password: String(data.Password__c ?? data.password ?? raw.Password__c ?? '').trim() || undefined,
    pinMailed: pinMailedVal === true || pinMailedVal === 'true' || pinMailedVal === 1,
    pin: String(data.Pin__c ?? data.PIN__c ?? data.pin ?? raw.Pin__c ?? raw.PIN__c ?? '').trim() || undefined,
    fourDigitPin: String(fourDigitPin ?? '').trim() || undefined,
    challengeQuestion: String(challengeQ ?? '').trim() || undefined,
    challengeAnswer: String(challengeA ?? '').trim() || undefined,
    notes: String(data.Notes__c ?? data.notes ?? raw.Notes__c ?? raw.notes ?? '').trim() || undefined,
    accountAlias: String(data.Account_Alias__c ?? data.accountAlias ?? raw.Account_Alias__c ?? '').trim() || undefined,
    blockProcessCollection: tri(blockProc),
    unblockProcessCollection: tri(unblockProc),
    createdBy:
      String(
        data[`CreatedBy${CUSTOM_SUFFIX}`] ?? data.CreatedBy__c ??
        data[`CreatedById${CUSTOM_SUFFIX}`] ?? data.CreatedById__c ??
        data.CreatedByName ?? data.createdBy ?? data.createdByName ??
        data[`created_by_name${CUSTOM_SUFFIX}`] ?? data.created_by_name ??
        raw.CreatedBy__c ?? ''
      ).trim() || undefined,
    createdDate:
      String(
        data[`CreatedDate${CUSTOM_SUFFIX}`] ?? data.CreatedDate__c ??
        data.createdDate ?? raw.CreatedDate__c ?? ''
      ).trim() || undefined,
    lastModifiedBy:
      String(
        data[`LastModifiedBy${CUSTOM_SUFFIX}`] ?? data.LastModifiedBy__c ??
        data[`LastModifiedById${CUSTOM_SUFFIX}`] ?? data.LastModifiedById__c ??
        data.LastModifiedByName ?? data.lastModifiedBy ?? data.lastModifiedByName ??
        data[`last_modified_by_name${CUSTOM_SUFFIX}`] ?? data.last_modified_by_name ??
        data[`LastModifiedByAlias${CUSTOM_SUFFIX}`] ?? data.LastModifiedByAlias__c ??
        raw.LastModifiedBy__c ?? ''
      ).trim() || undefined,
    lastModifiedDate:
      String(
        data[`LastModifiedDate${CUSTOM_SUFFIX}`] ?? data.LastModifiedDate__c ??
        data.lastModifiedDate ?? raw.LastModifiedDate__c ?? ''
      ).trim() || undefined,
    requestNotes: String(data.Request__c ?? data.requestNotes ?? raw.Request__c ?? '').trim() || undefined,
  };
};

export type BureauCredentialsMap = Record<(typeof BUREAUS)[number], BureauCredential>;

export function useCreditBlockBureaus(
  clientId: string,
  enabled = true,
  placeholderData?: BureauCredentialsMap
) {
  return useQuery({
    queryKey: ['credit-block', 'bureaus', clientId],
    placeholderData,
    queryFn: async (): Promise<BureauCredentialsMap> => {
      let forClient: Record<string, unknown>[] = [];
      try {
        forClient = await getBureausViaTraversalFromClient(clientId);
      } catch {
        forClient = [];
      }
      // Start from list/join placeholder so a failed or empty clientId match does not flash then wipe the tab.
      const result: BureauCredentialsMap = {
        chexsystems: { ...(placeholderData?.chexsystems ?? {}) },
        equifax: { ...(placeholderData?.equifax ?? {}) },
        experian: { ...(placeholderData?.experian ?? {}) },
        transunion: { ...(placeholderData?.transunion ?? {}) },
      };
      for (const r of forClient) {
        const bureau = normalizeBureau(
          r.Bureau_Name__c ?? r.Bureau__c ?? r.bureau ?? r.Name__c ?? r.name
        );
        const mapped = mapToBureauCredential(r);
        result[bureau] = { ...result[bureau], ...mapped };
      }

      // Resolve SF user IDs to display names in createdBy / lastModifiedBy
      const allCreds = Object.values(result);
      const sfIds = new Set<string>();
      for (const c of allCreds) {
        if (looksLikeGraphUserSfId(c.createdBy)) sfIds.add(c.createdBy!);
        if (looksLikeGraphUserSfId(c.lastModifiedBy)) sfIds.add(c.lastModifiedBy!);
      }
      if (sfIds.size > 0) {
        try {
          const nameMap = await resolveGraphUserNamesBySfIds(Array.from(sfIds));
          for (const c of allCreds) {
            const createdName = c.createdBy ? nameMap.get(c.createdBy) : undefined;
            if (createdName) c.createdBy = createdName;
            const modifiedName = c.lastModifiedBy ? nameMap.get(c.lastModifiedBy) : undefined;
            if (modifiedName) c.lastModifiedBy = modifiedName;
          }
        } catch {
          // Silently fall back to raw IDs
        }
      }

      return result;
    },
    enabled: USE_GOBODO_API && !!clientId && enabled,
    staleTime: 60_000,
  });
}

/**
 * Maps a BureauCredential field key to the Gobodo field name.
 */
const fieldToGobodo: Record<string, string> = {
  status: 'Stage__c',
  authority: 'Authority__c',
  username: 'Username__c',
  password: 'Password__c',
  pin: 'PIN__c',
  pinMailed: 'PINsMailed__c',
  startDate: 'StartDate__c',
  enrollmentDate: 'EnrollmentDate__c',
  endDate: 'EndDate__c',
  unblockDate: 'UnblockDate__c',
  portalCreationDate: 'PortalCreationDate__c',
  permUnblockDate: 'PermUnblockDate__c',
  challengeQuestion: 'ChallengeQuestion__c',
  fourDigitPin: 'FourDigitPIN__c',
  challengeAnswer: 'ChallengeAnswer__c',
  unblockRequestStatus: 'UnblockRequestStatus__c',
  notes: 'Notes__c',
  accountAlias: 'Account_Alias__c',
  blockProcessCollection: 'Block_Process_Collection__c',
  unblockProcessCollection: 'Unblock_Process_Collection__c',
  requestNotes: 'Request__c',
};

function toCanonicalStage(value: unknown): string {
  const s = String(value ?? '').trim().toLowerCase();
  if (s === 'locked' || s === 'blocked') return 'Locked';
  if (s === 'unlocked' || s === 'unblocked') return 'Unlocked';
  return String(value ?? '').trim();
}

function toGobodoUpdates(updates: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    const gobodoKey = fieldToGobodo[key] || key;
    const normalizedValue = key === 'status' ? toCanonicalStage(value) : value;
    result[gobodoKey] = normalizedValue;
    if (CUSTOM_SUFFIX && CUSTOM_SUFFIX !== '__c' && gobodoKey.endsWith('__c')) {
      result[gobodoKey.replace(/__c$/i, CUSTOM_SUFFIX)] = normalizedValue;
    }
    if (gobodoKey === 'PINsMailed__c') {
      result.PIN_Mailed__c = normalizedValue;
    }
    if (gobodoKey === 'StartDate__c') {
      result.Start_Date__c = normalizedValue;
    }
    if (gobodoKey === 'EndDate__c') {
      result.End_Date__c = normalizedValue;
    }
  }
  return result;
}

/**
 * Update a single bureau field. Routes through recordStorageService
 * so the workflow trigger hook fires (e.g., locking a bureau triggers
 * the "Bureau Locked" workflow notification).
 */
export function useUpdateBureauField(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bureau,
      creditBureauId,
      fieldKey,
      value,
    }: {
      bureau: (typeof BUREAUS)[number];
      creditBureauId: string;
      fieldKey: string;
      value: unknown;
    }) => {
      const gobodoUpdates = toGobodoUpdates({ [fieldKey]: value });
      if (fieldKey === 'startDate') gobodoUpdates.EnrollmentDate__c = value;
      if (fieldKey === 'endDate') gobodoUpdates.UnblockDate__c = value;

      // When stage changes to Blocked/Locked, clear unblock start/end dates
      if (fieldKey === 'status') {
        const normalized = String(value ?? '').trim().toLowerCase();
        if (normalized === 'locked' || normalized === 'blocked') {
          gobodoUpdates.StartDate__c = '';
          gobodoUpdates.Start_Date__c = '';
          gobodoUpdates.EnrollmentDate__c = '';
          gobodoUpdates.EndDate__c = '';
          gobodoUpdates.End_Date__c = '';
          gobodoUpdates.UnblockDate__c = '';
          if (CUSTOM_SUFFIX !== '__c') {
            gobodoUpdates[`StartDate${CUSTOM_SUFFIX}`] = '';
            gobodoUpdates[`Start_Date${CUSTOM_SUFFIX}`] = '';
            gobodoUpdates[`EnrollmentDate${CUSTOM_SUFFIX}`] = '';
            gobodoUpdates[`EndDate${CUSTOM_SUFFIX}`] = '';
            gobodoUpdates[`End_Date${CUSTOM_SUFFIX}`] = '';
            gobodoUpdates[`UnblockDate${CUSTOM_SUFFIX}`] = '';
          }
        }
      }

      const updated = await updateRecord(BUREAU_OBJECT, creditBureauId, gobodoUpdates, { gobodoPropertyPatch: true });
      return { bureau, updated };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'bureaus', clientId] });
    },
  });
}

/**
 * Batch-save all bureau credential changes for a client.
 */
export function useSaveBureauCredentials(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      bureauUpdates: Array<{
        bureau: (typeof BUREAUS)[number];
        creditBureauId: string;
        changes: Record<string, unknown>;
      }>
    ) => {
      const results = [];
      for (const { creditBureauId, changes } of bureauUpdates) {
        if (!creditBureauId || Object.keys(changes).length === 0) continue;
        const gobodoUpdates = toGobodoUpdates(changes);
        const updated = await updateRecord(BUREAU_OBJECT, creditBureauId, gobodoUpdates, { gobodoPropertyPatch: true });
        results.push(updated);
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'bureaus', clientId] });
    },
  });
}

/**
 * Batch-update bureau Stage__c for multiple bureaus in parallel.
 * Used by toolbar button handlers (Block, Unblock, Extension, Permanent Unblock).
 */
export function useUpdateBureauStages(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updates: Array<{
        bureau: (typeof BUREAUS)[number];
        creditBureauId: string;
        stage: 'Locked' | 'Unlocked';
        startDate?: string;
        endDate?: string;
        permUnblockDate?: string;
      }>
    ) => {
      const results = await Promise.allSettled(
        updates
          .filter((u) => u.creditBureauId)
          .map(async ({ creditBureauId, stage, startDate, endDate, permUnblockDate }) => {
            const payload: Record<string, unknown> = {
              Stage__c: stage,
              ...(CUSTOM_SUFFIX !== '__c' ? { [`Stage${CUSTOM_SUFFIX}`]: stage } : {}),
            };
            if (startDate) {
              payload.StartDate__c = startDate;
              payload.Start_Date__c = startDate;
              payload.EnrollmentDate__c = startDate;
              if (CUSTOM_SUFFIX !== '__c') {
                payload[`StartDate${CUSTOM_SUFFIX}`] = startDate;
                payload[`Start_Date${CUSTOM_SUFFIX}`] = startDate;
                payload[`EnrollmentDate${CUSTOM_SUFFIX}`] = startDate;
              }
            }
            if (endDate) {
              payload.EndDate__c = endDate;
              payload.End_Date__c = endDate;
              payload.UnblockDate__c = endDate;
              if (CUSTOM_SUFFIX !== '__c') {
                payload[`EndDate${CUSTOM_SUFFIX}`] = endDate;
                payload[`End_Date${CUSTOM_SUFFIX}`] = endDate;
                payload[`UnblockDate${CUSTOM_SUFFIX}`] = endDate;
              }
            }
            if (permUnblockDate) {
              payload.PermUnblockDate__c = permUnblockDate;
              if (CUSTOM_SUFFIX !== '__c') {
                payload[`PermUnblockDate${CUSTOM_SUFFIX}`] = permUnblockDate;
              }
            }
            return updateRecord(BUREAU_OBJECT, creditBureauId, payload, { gobodoPropertyPatch: true });
          })
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0 && failed.length === results.length) {
        throw new Error(`All ${failed.length} bureau stage updates failed`);
      }
      return { total: results.length, failed: failed.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'bureaus', clientId] });
    },
  });
}

const HISTORY_OBJECT = `Unlock_Request${CUSTOM_SUFFIX}`;

/**
 * Create a new unlock/lock request record. Routes through recordStorageService
 * so the "Unlock Request Filed" workflow triggers (creates reblock case + task).
 */
function requestTypeToGobodoPicklist(
  t:
    | 'lock_request'
    | 'unlock_request'
    | 'permanent_block'
    | 'permanent_unblock'
    | 'extension'
    | 'special_case'
): string {
  switch (t) {
    case 'unlock_request':
      return 'Unblock';
    case 'permanent_unblock':
      return 'Permanent Unblock';
    case 'special_case':
      return 'Special Case';
    case 'permanent_block':
      return 'Permanent Block';
    case 'extension':
      return 'Extension';
    default:
      return 'Block';
  }
}

function bureauToDisplayName(b: (typeof BUREAUS)[number]): string {
  return b.charAt(0).toUpperCase() + b.slice(1);
}

export type CreateUnlockRequestPayload = {
  bureau: (typeof BUREAUS)[number] | 'all_three';
  requestType:
    | 'lock_request'
    | 'unlock_request'
    | 'permanent_block'
    | 'permanent_unblock'
    | 'extension'
    | 'special_case';
  reason?: string;
  startDate?: string;
  endDate?: string;
  caseId?: string;
};

/**
 * Create Unlock_Request__c rows (same shape as client-detail “New bureau request”).
 * Use from header case modal after Case__c is created.
 */
export async function createUnlockRequestRecordsForClient(
  clientId: string,
  data: CreateUnlockRequestPayload
): Promise<unknown[]> {
  const cid = clientId?.trim();
  if (!cid) throw new Error('Client is required');

  const bureaus: (typeof BUREAUS)[number][] =
    data.bureau === 'all_three' ? ['equifax', 'experian', 'transunion'] : [data.bureau];
  const requestTypeLabel = requestTypeToGobodoPicklist(data.requestType);
  const results: unknown[] = [];
  const caseIdTrim = data.caseId?.trim();
  let lastUnlockKey = '';
  for (const bureau of bureaus) {
    const reasonParts = [
      data.reason?.trim() || '',
      data.startDate && `Start: ${data.startDate}`,
      data.endDate && `End: ${data.endDate}`,
    ].filter(Boolean);
    const recordData: Record<string, unknown> = {
      Request_Type__c: requestTypeLabel,
      [`Request_Type${CUSTOM_SUFFIX}`]: requestTypeLabel,
      Bureau_Name__c: bureauToDisplayName(bureau),
      [`Bureau_Name${CUSTOM_SUFFIX}`]: bureauToDisplayName(bureau),
      Reason__c: reasonParts.join(' · '),
      [`Reason${CUSTOM_SUFFIX}`]: reasonParts.join(' · '),
      Status__c: 'Pending',
      [`Status${CUSTOM_SUFFIX}`]: 'Pending',
      [`ClientRelField${CUSTOM_SUFFIX}`]: cid,
      Request_Date__c: new Date().toISOString(),
      [`Request_Date${CUSTOM_SUFFIX}`]: new Date().toISOString(),
    };
    if (data.caseId?.trim()) {
      recordData.CaseId__c = data.caseId.trim();
      recordData.RelatedCase__c = data.caseId.trim();
      recordData[`CaseRelField${CUSTOM_SUFFIX}`] = data.caseId.trim();
    }
    if (data.startDate) {
      recordData.Start_Date__c = data.startDate;
      recordData.Unlock_Start_Date__c = data.startDate;
      if (CUSTOM_SUFFIX !== '__c') {
        recordData[`Start_Date${CUSTOM_SUFFIX}`] = data.startDate;
        recordData[`Unlock_Start_Date${CUSTOM_SUFFIX}`] = data.startDate;
      }
    }
    if (data.endDate) {
      recordData.End_Date__c = data.endDate;
      recordData.Unlock_End_Date__c = data.endDate;
      if (CUSTOM_SUFFIX !== '__c') {
        recordData[`End_Date${CUSTOM_SUFFIX}`] = data.endDate;
        recordData[`Unlock_End_Date${CUSTOM_SUFFIX}`] = data.endDate;
      }
    }
    const created = await createRecord(HISTORY_OBJECT, recordData);
    results.push(created);
    const unlockKey = graphKeyFromCreatedRecord(created as { id?: unknown; key?: unknown });
    if (unlockKey) lastUnlockKey = unlockKey;

    // Log activity on the related case
    if (caseIdTrim) {
      logRecordCreated(caseIdTrim, `Case${CUSTOM_SUFFIX}`, '', '', {
        action: 'related_added',
        relatedObjectType: HISTORY_OBJECT,
        relatedObjectId: unlockKey || String((created as { id?: unknown }).id ?? ''),
        description: `${requestTypeLabel} request created for ${bureauToDisplayName(bureau)}${data.startDate ? ` (${data.startDate}${data.endDate ? ` – ${data.endDate}` : ''})` : ''}`,
      });
    }
  }
  // Link Case → Unlock_Request once (last bureau row wins when multiple UR per case).
  if (caseIdTrim && lastUnlockKey) {
    try {
      await updateRecord(
        `Case${CUSTOM_SUFFIX}`,
        caseIdTrim,
        {
          [UNLOCK_REQUEST_REL_FIELD_ON_CASE]: lastUnlockKey,
        },
        { gobodoPropertyPatch: true }
      );
    } catch {
      /* graph field may be missing in older envs; CaseRelField on UR still holds Case link */
    }
  }
  return results;
}

export function useCreateUnlockRequest(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUnlockRequestPayload) => createUnlockRequestRecordsForClient(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'unlock-requests'] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'bureaus', clientId] });
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
    },
  });
}
